import React from "react";
import ProductItem from "./ProductSimpleItem/ProductItem";
import { FmShortSummary } from "../FmShortSummary";
import { FmRightDrawer } from "../../Components/FmRightDrawer";
import { useTranslation } from "react-i18next";
import { ShortProduct, BlendStat } from "../../Models";
import Configs from "../../Configs";
import { useSelector } from "react-redux";
import { AgriSolutionState, GeneralState } from "../../Redux";

import useStyles from "./FmSummary.style";
import { BIG_MESURMENT_UNIT } from "../../Constants";
import { links } from "../../Utils/Consts";
import { shareableItemTypes } from "../../Utils";
interface FmSummaryProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    products?: ShortProduct[];
    isEditMode: boolean;
}

const FmSummary = ({ isOpen, setIsOpen, products, isEditMode = false }: FmSummaryProps) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const cropsAssets = useSelector(GeneralState.Selectors.selectCrops);

    const stepIndexPage = useSelector(AgriSolutionState.Selectors.selectStepIndexPage);
    const isAgri = useSelector(GeneralState.Selectors.selectIsAgri);

    const cropState = useSelector(AgriSolutionState.Selectors.selectCropsState);

    const soilState = useSelector(AgriSolutionState.Selectors.selectSoilState);

    const resultState = useSelector(AgriSolutionState.Selectors.selectResultState);

    const customizedData = useSelector(AgriSolutionState.Selectors.selectCustomizedState);

    const recommendedMaterialCode = useSelector(AgriSolutionState.Selectors.selectRecommendedMaterialCode);

    const recommendedSolutionName = useSelector(AgriSolutionState.Selectors.selectRecommendedSolutionName);

    const { activeMetricMethod } = useSelector(GeneralState.Selectors.selectMetricMethods);
    const valueSelected = activeMetricMethod === "1" ? 0 : 1;

    const isSolutionShow = !!recommendedSolutionName;
    const cropImage = cropsAssets.find((i: any) => i.code == cropState.selectedCrop);
    const displayReleaseGraphType = useSelector(AgriSolutionState.Selectors.selectDisplayReleaseGraphType);
    const graphIsCustomized = displayReleaseGraphType == shareableItemTypes.shareableItemTypes.agri_multicote_customize;

    const cropData = [
        {
            title: t("PDFfile.summaryData.cropProp"),
            values: [t(`crops.${cropState.selectedCrop as string}`)],
            image: cropImage && cropImage.image != "Generic.png" ? `${Configs.cropImagesPath}/${cropImage.image}` : null,
        },
        {
            title: t("PDFfile.summaryData.yieldProp"),
            values: [cropState.expectedYield && `${cropState.expectedYield} ${(BIG_MESURMENT_UNIT as any)[activeMetricMethod]}`],
        },
        {
            title: t("PDFfile.summaryData.plantingDateProp"),
            values: [cropState.plantingDate as string],
        },
        {
            title: t("PDFfile.summaryData.harvestDateProp"),
            values: [cropState.finalHarvestDate as string],
        },
    ];

    const soilData = [
        {
            title: t("PDFfile.summaryData.WaterRegimeProp"),
            values: [t(`agriSolution.waterRegime.sliderMarks.${soilState.selectedWater}`)],
        },
        {
            title: t("PDFfile.summaryData.soilProp"),
            values: [t(`agriSolution.soilType.sliderMarks.${soilState.selectedSoil}`)],
        },
    ];

    const solutionData = [
        {
            title: t("PDFfile.summaryData.recommended"),
            values: [recommendedSolutionName],
        },
        {
            title: t("PDFfile.summaryData.MaterialCodeProp"),
            values: [recommendedMaterialCode],
        },
        {
            title: t("PageReleaseChart.sendToAgronomist.preVolumeApplication"),
            values: [
                resultState.recommendedSolution
                    ? `${resultState.customizedSolution?.applicationVolume[valueSelected].value} ${resultState.customizedSolution?.applicationVolume[valueSelected].mesurmentUnit}`
                    : "",
            ],
        },
    ];

    const getCoatedItemsFromRecipe = (): string[] => {
        const recipe: string[] = [];
        customizedData.recipe.coated.forEach((i: BlendStat) => {
            recipe.push(`${i.name} | ${i.precentage}%`);
        });
        return recipe;
    };
    const getUncoatedItemsFromRecipe = (): string[] => {
        const recipe: string[] = [];
        customizedData.recipe.unCoated.forEach((i: BlendStat) => {
            recipe.push(`${i.name} | ${i.precentage}%`);
        });
        return recipe;
    };
    const graphData = [
        {
            title: t("fmSummary.recipeCoatedElements"),
            values: getCoatedItemsFromRecipe(),
        },
        {
            title: t("fmSummary.recipeUncoatedElements"),
            values: getUncoatedItemsFromRecipe(),
        },
    ];
    return (
        <FmRightDrawer
            title={stepIndexPage === 0 ? t("fmSummary.ourProducts") : t("fmSummary.summary")}
            isOpen={isOpen}
            setIsOpen={(i: boolean) => setIsOpen && setIsOpen(i)}
            isEditMode={isEditMode}
        >
            {stepIndexPage !== 0 && isAgri && (
                <div className={classes.shortSummary_container}>
                    {stepIndexPage >= 1 && <FmShortSummary label={t("fmSummary.shortSummaryTitle1")} data={cropData} />}
                    {stepIndexPage >= 2 && <FmShortSummary label={t("fmSummary.shortSummaryTitle2")} data={soilData} />}
                    {stepIndexPage >= 2.5 && !graphIsCustomized && <FmShortSummary label={t("fmSummary.shortSummaryTitle3")} data={solutionData} />}
                    {graphIsCustomized && stepIndexPage === 2.5 && <FmShortSummary label={t("fmSummary.shortSummaryTitleGraphPage")} data={graphData} />}
                </div>
            )}
            {products && (
                <div>
                    {stepIndexPage !== 0 && <span className={classes.title}>{t("fmSummary.ourProducts")}</span>}
                    <div className={classes.productsList}>
                        {products.map((product: ShortProduct) => (
                            <ProductItem
                                key={product.name}
                                img={`${Configs.imagesPath}/${product.image}` || Configs.productPlaceHolderImage}
                                title={product.name}
                                description={t(`products.${product.descriptionKey}`)}
                                link={product.link}
                            />
                        ))}
                    </div>
                    <div className={classes.see_more}>
                        <a href={links.controlledReleaseFertilizer} target='_blank' rel='noreferrer noopener' className={classes.see_more_link}>
                            {t("fmSummary.seeMoreProducts")}
                        </a>
                    </div>
                </div>
            )}
        </FmRightDrawer>
    );
};

export default FmSummary;
